import clsx from 'clsx'
import type { Image as ExpoImage, ImageProps as ExpoImageProps, Image, ImageProps } from 'expo-image'
import { useMemo, useRef, useState, type ForwardedRef } from 'react'

import { useOnChange } from '@/src/general/hooks/useOnChange'
import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'

import { image, type TImageVariantProps } from './variants/image-variants'

type TStatus = 'loading' | 'failed' | 'pending' | 'loaded'

export type TImageProps = ExpoImageProps & {
  /**
   * Ref to the DOM/react-native node.
   */
  ref?: ForwardedRef<ExpoImage>
  /**
   * Whether to add a blurred effect to the image.
   * @default false
   */
  isBlurred?: boolean
  /**
   * Controlled loading state.
   */
  isLoading?: boolean
} & TImageVariantProps

export function useImageNative(originalProps: TImageProps) {
  const [props, variantProps] = mapPropsVariants(originalProps, image.variantKeys)

  const {
    ref: refProp,
    className,
    isBlurred,
    blurRadius = isBlurred ? 10 : undefined,
    isLoading: isLoadingProp,
    onError,
    onLoad,
    source,
    contentFit = 'contain',
    transition = 450,
    ...otherProps
  } = props

  const fallbackRef = useRef<Image>(null)
  const ref = refProp || fallbackRef

  const [imageStatus, setImageStatus] = useState<TStatus>(source ? 'loading' : 'pending')

  useOnChange(source, () => {
    setImageStatus(source ? 'loading' : 'pending')
  })

  const isLoading = imageStatus === 'loading' || isLoadingProp

  const imageStyle = useMemo(() => image(variantProps), [variantProps])

  const getImgProps = (newProps: Partial<TImageProps> = {}): ImageProps => ({
    ref,
    source,
    blurRadius,
    contentFit,
    transition,
    ...otherProps,
    ...newProps,
    onLoad: (e) => {
      setImageStatus('loaded')
      onLoad?.(e)
      newProps?.onLoad?.(e)
    },
    onError: (e) => {
      setImageStatus('failed')
      onError?.(e)
      newProps?.onError?.(e)
    },
    className: clsx(imageStyle, className),
  })

  return {
    isLoading,
    getImgProps,
  }
}
